/**
 * HistoryTab -- Watch history tab for the UserAdminPanel.
 *
 * Shows time range selector, geo map hero with attached summary stats,
 * stacked-bar breakdowns (media type, play method, device), and a compact
 * recent plays list.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { Icon } from "../../ui/Icon";
import { Spinner } from "../../ui/Spinner";
import { cn } from "../../../utils/cn";
import {
  truncateText,
  TRUNCATE_MEDIA_TITLE,
  TRUNCATE_DEVICE_NAME,
} from "../../../utils/truncateText";
import type {
  HistoryWindow,
  UserWatchHistory,
} from "../../../hooks/useUserProfile";
import { GeoPlaybackHistory } from "../GeoPlaybackHistory";
import { formatDuration, formatRelativeTime } from "./helpers";

// ============================================================================
// Constants
// ============================================================================

const WINDOW_OPTIONS: { value: HistoryWindow; label: string }[] = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "180d", label: "180d" },
  { value: "all", label: "All" },
];

const MEDIA_TYPE_SEGMENTS: {
  key: string;
  label: string;
  color: string;
}[] = [
  { key: "Movie", label: "Movie", color: "bg-violet-500" },
  { key: "Episode", label: "Episode", color: "bg-sky-500" },
  { key: "Live TV", label: "Live TV", color: "bg-amber-500" },
  { key: "Audio", label: "Audio", color: "bg-rose-500" },
];

const PLAY_METHOD_SEGMENTS: {
  key: string;
  label: string;
  color: string;
}[] = [
  { key: "DirectPlay", label: "Direct Play", color: "bg-emerald-500" },
  { key: "DirectStream", label: "Direct Stream", color: "bg-sky-500" },
  { key: "Transcode", label: "Transcode", color: "bg-amber-500" },
];

/** Rotating palette for dynamic device segments */
const DEVICE_COLORS = [
  "bg-sky-500",
  "bg-emerald-500",
  "bg-violet-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-slate-400",
];

// ============================================================================
// Types
// ============================================================================

export type HistoryTabProps = {
  history: UserWatchHistory | null;
  historyLoading: boolean;
  historyError: string | null;
  historyWindow: HistoryWindow;
  onWindowChange: (w: HistoryWindow) => void;
};

// ============================================================================
// Chart section header
// ============================================================================

const ChartHeader = ({ children }: { children: React.ReactNode }) => (
  <h4 className="text-[10px] 2xl:text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground/70 mb-2">
    {children}
  </h4>
);

// ============================================================================
// Stacked Bar Breakdown
// ============================================================================

type BreakdownSegment = {
  label: string;
  color: string;
  count: number;
  watchTimeSecs: number;
  pct: number;
};

const StackedBreakdown = ({
  title,
  segments,
}: {
  title: string;
  segments: BreakdownSegment[];
}) => {
  const hasData = segments.some((s) => s.count > 0);
  if (!hasData) {
    return (
      <div className="rounded-lg border border-border/50 bg-card p-3">
        <ChartHeader>{title}</ChartHeader>
        <p className="text-[10px] text-muted-foreground">No data</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border/50 bg-card p-3">
      <ChartHeader>{title}</ChartHeader>

      {/* Stacked bar */}
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted/20">
        {segments.map((seg) =>
          seg.count > 0 ? (
            <div
              key={seg.label}
              className={`${seg.color} transition-all`}
              style={{ width: `${seg.pct}%` }}
            />
          ) : null,
        )}
      </div>

      {/* Legend dots */}
      <div className="flex flex-wrap items-center gap-3 mt-1.5">
        {segments.map((seg) => (
          <span
            key={seg.label}
            className="flex items-center gap-1.5 text-[10px] text-muted-foreground"
          >
            <span
              className={`inline-block h-2 w-2 rounded-full ${seg.color}`}
            />
            {seg.label}: {seg.pct}%
          </span>
        ))}
      </div>

      {/* Watch time per category */}
      <div className="mt-2 space-y-0.5">
        {segments
          .filter((s) => s.count > 0)
          .map((seg) => (
            <div
              key={seg.label}
              className="flex items-center justify-between text-[10px] text-muted-foreground"
            >
              <span>{seg.label}</span>
              <span className="tabular-nums">
                {formatDuration(seg.watchTimeSecs)} ({seg.count})
              </span>
            </div>
          ))}
      </div>
    </div>
  );
};

// ============================================================================
// Component
// ============================================================================

export const HistoryTab = ({
  history,
  historyLoading,
  historyError,
  historyWindow,
  onWindowChange,
}: HistoryTabProps) => {
  const items = history?.items ?? [];
  const summary = history?.summary ?? null;

  // -- Breakdown data ---------------------------------------------------------

  const deviceSegments = useMemo((): BreakdownSegment[] => {
    const raw = history?.byDevice ?? [];
    const total = raw.reduce((sum, d) => sum + d.sessionsCount, 0);
    if (total === 0) return [];

    return raw.map((d, i) => ({
      label: d.deviceName,
      color: DEVICE_COLORS[i % DEVICE_COLORS.length]!,
      count: d.sessionsCount,
      watchTimeSecs: d.watchTimeSecs,
      pct: Math.round((d.sessionsCount / total) * 100),
    }));
  }, [history]);

  const mediaTypeSegments = useMemo((): BreakdownSegment[] => {
    const raw = history?.byMediaType ?? [];
    const total = raw.reduce((sum, m) => sum + m.sessionsCount, 0);
    if (total === 0) return [];

    // Map known types, collect unknown into "Other"
    const known = new Map(raw.map((m) => [m.mediaType, m]));
    const segments: BreakdownSegment[] = [];

    for (const cfg of MEDIA_TYPE_SEGMENTS) {
      const entry = known.get(cfg.key);
      segments.push({
        label: cfg.label,
        color: cfg.color,
        count: entry?.sessionsCount ?? 0,
        watchTimeSecs: entry?.watchTimeSecs ?? 0,
        pct: entry ? Math.round((entry.sessionsCount / total) * 100) : 0,
      });
      known.delete(cfg.key);
    }

    // Remaining unknown types
    let otherCount = 0;
    let otherTime = 0;
    for (const [, v] of known) {
      otherCount += v.sessionsCount;
      otherTime += v.watchTimeSecs;
    }
    if (otherCount > 0) {
      segments.push({
        label: "Other",
        color: "bg-slate-400",
        count: otherCount,
        watchTimeSecs: otherTime,
        pct: Math.round((otherCount / total) * 100),
      });
    }

    return segments;
  }, [history]);

  const playMethodSegments = useMemo((): BreakdownSegment[] => {
    const raw = history?.byPlayMethod ?? [];
    const total = raw.reduce((sum, p) => sum + p.sessionsCount, 0);
    if (total === 0) return [];

    const known = new Map(raw.map((p) => [p.playMethod, p]));
    const segments: BreakdownSegment[] = [];

    for (const cfg of PLAY_METHOD_SEGMENTS) {
      const entry = known.get(cfg.key);
      segments.push({
        label: cfg.label,
        color: cfg.color,
        count: entry?.sessionsCount ?? 0,
        watchTimeSecs: entry?.watchTimeSecs ?? 0,
        pct: entry ? Math.round((entry.sessionsCount / total) * 100) : 0,
      });
    }

    return segments;
  }, [history]);

  // -- Render -----------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* 1. Time Range Selector */}
      <div
        className="flex items-center gap-1"
        role="group"
        aria-label="History time range"
      >
        {WINDOW_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onWindowChange(opt.value)}
            className={cn(
              "px-2.5 py-1 rounded-md text-[10px] 2xl:text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70",
              historyWindow === opt.value
                ? "bg-primary/15 text-primary border border-primary/30"
                : "bg-muted/20 text-muted-foreground border border-border/30 hover:bg-muted/40",
            )}
          >
            {opt.label}
          </button>
        ))}
        {historyLoading && <Spinner size="3" className="ml-1" />}
      </div>

      {/* Error (non-fatal) */}
      {historyError && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 px-2.5 py-1.5 text-[10px] 2xl:text-xs text-destructive">
          Watch history: {historyError}
        </div>
      )}

      {/* 2. Geo Map Hero + Summary Stats Footer */}
      <div className="rounded-lg border border-border/50 overflow-hidden">
        <GeoPlaybackHistory history={history} historyLoading={historyLoading} />

        {summary && (
          <div className="grid grid-cols-3 divide-x divide-border/30 border-t border-border/30 bg-card">
            <div className="px-4 py-2.5 text-center">
              <div className="text-lg 2xl:text-xl font-bold text-foreground tabular-nums">
                {formatDuration(summary.totalWatchTimeSecs)}
              </div>
              <div className="text-[10px] 2xl:text-xs text-muted-foreground">
                Watch Time
              </div>
            </div>
            <div className="px-4 py-2.5 text-center">
              <div className="text-lg 2xl:text-xl font-bold text-foreground tabular-nums">
                {summary.sessionsCount.toLocaleString()}
              </div>
              <div className="text-[10px] 2xl:text-xs text-muted-foreground">
                Sessions
              </div>
            </div>
            <div className="px-4 py-2.5 text-center">
              <div className="text-lg 2xl:text-xl font-bold text-foreground tabular-nums">
                {Math.round(summary.avgCompletionPct)}%
              </div>
              <div className="text-[10px] 2xl:text-xs text-muted-foreground">
                Avg Completion
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3. Metrics + Recent Plays -- 2 column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] lg:items-start gap-3">
        {/* Left: Three metrics stacked */}
        <div className="space-y-3">
          <StackedBreakdown
            title="By Media Type"
            segments={mediaTypeSegments}
          />
          <StackedBreakdown
            title="By Play Method"
            segments={playMethodSegments}
          />
          <StackedBreakdown title="By Device" segments={deviceSegments} />
        </div>

        {/* Right: Recent Plays — stretches to match left column height */}
        <div className="rounded-lg border border-border/50 bg-card p-3 flex flex-col lg:self-stretch min-h-0">
          <ChartHeader>
            Recent Plays
            {history && (
              <span className="ml-1 text-muted-foreground/50 normal-case tracking-normal font-normal">
                ({history.total} total)
              </span>
            )}
          </ChartHeader>

          {historyLoading && !history ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground flex-1">
              <Spinner size="4" className="mr-2" />
              <span className="text-xs">Loading...</span>
            </div>
          ) : items.length > 0 ? (
            <div className="flex-1 min-h-0 overflow-y-auto qb-scroll pr-1">
              {items.slice(0, 20).map((play) => (
                <RecentPlayRow key={play.id} play={play} />
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-6 text-muted-foreground flex-1">
              <span className="text-xs">No watch history in this period</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Recent Play Row (compact inline list item)
// ============================================================================

type PlayEntry = NonNullable<UserWatchHistory["items"]>[number];

const RecentPlayRow = ({ play }: { play: PlayEntry }) => {
  const mediaIcon =
    play.mediaType === "Movie"
      ? "lucide:film"
      : play.mediaType === "Episode"
        ? "lucide:tv"
        : play.mediaType === "Live TV"
          ? "lucide:radio"
          : play.mediaType === "Audio"
            ? "lucide:music"
            : "lucide:play";

  let displayTitle = play.mediaTitle;
  const isLiveTv = play.mediaType === "Live TV";

  if (
    play.mediaType === "Episode" &&
    play.seriesName &&
    play.seasonNumber != null
  ) {
    const se = `S${String(play.seasonNumber).padStart(2, "0")}E${String(play.episodeNumber ?? 0).padStart(2, "0")}`;
    displayTitle = `${play.seriesName} ${se}`;
  } else if (isLiveTv && play.nowPlaying) {
    displayTitle = `${play.mediaTitle} - ${play.nowPlaying}`;
  }

  const completion = play.completionPct;

  return (
    <div className="flex items-center justify-between text-xs py-1">
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        <Icon
          icon={mediaIcon}
          className="h-3 w-3 text-muted-foreground shrink-0"
        />
        {play.mediaItemId && play.serverType ? (
          <Link
            to={`/streaming/dashboard/control-center/media/item/${play.serverType}/${play.mediaItemId}`}
            className="text-foreground truncate hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/70 rounded-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {truncateText(displayTitle, TRUNCATE_MEDIA_TITLE).displayText}
          </Link>
        ) : (
          <span className="text-foreground truncate">
            {truncateText(displayTitle, TRUNCATE_MEDIA_TITLE).displayText}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 ml-2 shrink-0">
        <span className="text-[10px] text-muted-foreground truncate max-w-[80px]">
          {truncateText(play.deviceName, TRUNCATE_DEVICE_NAME).displayText}
        </span>
        {!isLiveTv && (
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {Math.round(completion)}%
          </span>
        )}
        <span className="text-[10px] text-muted-foreground tabular-nums">
          {formatRelativeTime(play.startedAt)}
        </span>
      </div>
    </div>
  );
};
